import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CRM_STAGES } from "./crmConstants";

const EMPTY_CUSTOMER = {
  name: "", phone: "", email: "", vehicle_year: "", vehicle_make: "",
  vehicle_model: "", stage: "new_lead", tags: "", initial_note: "",
  next_follow_up: "", follow_up_note: "",
};

export const AddCustomerModal = ({ open, onOpenChange, onCreate }) => {
  const [form, setForm] = useState(EMPTY_CUSTOMER);
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const inputClass = "h-10 bg-[#111] border-white/10 text-white placeholder:text-zinc-600";

  const submit = async () => {
    if (!form.name || (!form.phone && !form.email)) return;
    setSaving(true);
    const ok = await onCreate({
      ...form,
      tags: form.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
      next_follow_up: form.next_follow_up || null,
    });
    setSaving(false);
    if (ok) {
      setForm(EMPTY_CUSTOMER);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-customer-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add customer</DialogTitle>
          <DialogDescription className="text-zinc-500">Create a lead, save their vehicle, and schedule the next follow-up.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-3">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Name *</Label><Input data-testid="new-customer-name" className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label><Input data-testid="new-customer-phone" className={inputClass} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label><Input data-testid="new-customer-email" type="email" className={inputClass} value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Year</Label><Input data-testid="new-customer-vehicle-year" className={inputClass} value={form.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Make</Label><Input data-testid="new-customer-vehicle-make" className={inputClass} value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Model</Label><Input data-testid="new-customer-vehicle-model" className={inputClass} value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Pipeline stage</Label>
            <Select value={form.stage} onValueChange={(value) => set("stage", value)}>
              <SelectTrigger data-testid="new-customer-stage" className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">{CRM_STAGES.map((stage) => <SelectItem data-testid={`new-customer-stage-${stage.value}`} key={stage.value} value={stage.value}>{stage.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Tags</Label><Input data-testid="new-customer-tags" className={inputClass} value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="VIP, Ceramic, Fleet" />
          </div>
          <div className="space-y-1.5">
            <Label>Follow-up date</Label><Input data-testid="new-customer-followup" type="date" className={inputClass} value={form.next_follow_up} onChange={(e) => set("next_follow_up", e.target.value)} />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Internal note</Label><Textarea data-testid="new-customer-note" className="bg-[#111] border-white/10 text-white" value={form.initial_note} onChange={(e) => set("initial_note", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="cancel-add-customer" type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">Cancel</button>
          <button data-testid="save-new-customer" type="button" disabled={saving || !form.name || (!form.phone && !form.email)} onClick={submit} className="rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-zinc-200 transition-colors">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Save customer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
